import React, { useState, useEffect } from 'react';
import { Plus, Send, Edit, Trash2, Check, X, Eye, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from '../icons';
import { styles, Modal } from '../theme';
import { API_URL, fetchWithAuth } from '../api';
import { usePermissions } from '../context/PermissionContext';

const isPeriodRestricted = (branchId, dateString, postedPeriods) => {
  if (!branchId || !dateString || !postedPeriods) return false;
  const date = new Date(dateString);
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  return postedPeriods.some(
    (p) => p.branch_id === parseInt(branchId) && p.month === month && p.year === year
  );
};

const getMinDate = (branchId, postedPeriods) => {
  if (!branchId || !postedPeriods || postedPeriods.length === 0) return '';
  const branchPostings = postedPeriods.filter(
    (p) => parseInt(p.branch_id) === parseInt(branchId)
  );
  if (branchPostings.length === 0) return '';
  const latest = branchPostings.sort((a, b) => {
    if (a.year !== b.year) return b.year - a.year;
    return b.month - a.month;
  })[0];
  const nextMonth = latest.month === 12 ? 1 : latest.month + 1;
  const nextYear = latest.month === 12 ? latest.year + 1 : latest.year;
  return `${nextYear}-${String(nextMonth).padStart(2, '0')}-01`;
};

const ChequeTransmittals = ({ token, postedPeriods }) => {
  const { canCreate, canUpdate, canApprove, canDelete, getModuleBranches } = usePermissions();
  const [transmittals, setTransmittals] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusSummary, setStatusSummary] = useState({ pending: 0, received: 0, total: 0 });
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [availableCheques, setAvailableCheques] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [viewingTransmittal, setViewingTransmittal] = useState(null);

  const [form, setForm] = useState({
    branch_id: '',
    status: 'pending',
    transmission_date: new Date().toISOString().split('T')[0],
    received_date: '',
    received_by: '',
    notes: '',
    cheques: []
  });

  const [filter, setFilter] = useState({
    branch_id: '',
    search: '',
    status: ''
  });

  useEffect(() => {
    fetchTransmittals(currentPage, perPage, searchQuery, filter.status, filter.branch_id);
    fetchManagementUsers();
    fetchBranches();
  }, [currentPage, perPage, searchQuery, filter.status, filter.branch_id]);


  const fetchTransmittals = async (page = 1, limit = 10, search = '', status = '', branchId = '') => {
    try {
      const queryParams = new URLSearchParams({
        page,
        per_page: limit,
        search: search,
        status: status,
        branch_id: branchId
      });

      const res = await fetchWithAuth(`${API_URL}/cheque-transmittals?${queryParams.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();

      setTransmittals(data.transmittals);
      setTotalCount(data.total_count);
      setTotalPages(data.total_pages);

      if (data.summary) {
        setStatusSummary({
          pending: data.summary.pending || 0,
          received: data.summary.received || 0,
          total: data.summary.total || 0
        });
      }
    } catch (err) {
      console.error('Error fetching transmittals:', err);
    }
  };

  const fetchManagementUsers = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/users/management`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setUsers(data);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const fetchBranches = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/branchesdropdown`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setBranches(data);
    } catch (err) {
      console.error('Error fetching branches:', err);
    }
  };

  const fetchContacts = async (branchId = '') => {
    try {
      const url = branchId
        ? `${API_URL}/contactsdropdown?branch_id=${branchId}`
        : `${API_URL}/contactsdropdown`;
      const res = await fetchWithAuth(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setContacts(data);
    } catch (err) {
      console.error('Error fetching contacts:', err);
    }
  };

  const fetchAvailableCheques = async (branchId) => {
    if (!branchId) {
      setAvailableCheques([]);
      return;
    }
    try {
      const res = await fetchWithAuth(`${API_URL}/cheque-book-items/available?branch_id=${branchId}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setAvailableCheques(data);
    } catch (err) {
      console.error('Error fetching available cheques:', err);
    }
  };

  const handleBranchChange = (branchId) => {
    setForm({ ...form, branch_id: branchId, cheques: [] });
    fetchAvailableCheques(branchId);
    fetchContacts(branchId);
  };

  const handleAddCheque = (chequeId) => {
    const cheque = availableCheques.find(c => c.id === parseInt(chequeId));
    if (!cheque) return;

    // Remove from available
    setAvailableCheques(availableCheques.filter(c => c.id !== cheque.id));

    // Add to form cheques
    setForm({
      ...form,
      cheques: [...form.cheques, {
        id: cheque.id,
        cheque_number: cheque.cheque_number,
        contact_id: cheque.contact_id || '',
        payee: cheque.payee || '',
        signatory: cheque.signatory || ''
      }]
    });
  };

  const handleRemoveCheque = (chequeId) => {
    const removedCheque = form.cheques.find(c => c.id === chequeId);
    if (!removedCheque) return;

    setForm({ ...form, cheques: form.cheques.filter(c => c.id !== chequeId) });

    // Add back to available cheques (with basic data, enough for dropdown)
    setAvailableCheques([...availableCheques, {
      id: removedCheque.id,
      cheque_number: removedCheque.cheque_number
    }].sort((a, b) => a.id - b.id));
  };

  const handleChequeFieldChange = (chequeId, field, value) => {
    const updatedCheques = form.cheques.map(c => {
      if (c.id === chequeId) {
        return { ...c, [field]: value };
      }
      return c;
    });
    setForm({ ...form, cheques: updatedCheques });
  };

  const handleSubmit = async () => {
    // Validation
    if (!form.branch_id || !form.transmission_date) {
      alert('Branch and Transmission Date are required');
      return;
    }

    if (isPeriodRestricted(form.branch_id, form.transmission_date, postedPeriods)) {
      alert("This period has been posted for the selected branch. No transactions can be created or updated.");
      return;
    }

    if (form.cheques.length === 0) {
      alert('At least one cheque must be attached to the transmittal');
      return;
    }

    for (const c of form.cheques) {
      if (!c.contact_id || !c.payee || !c.signatory) {
        alert(`Cheque ${c.cheque_number} is missing required details (Contact, Payee, and Signatory)`);
        return;
      }
    }

    try {
      const url = editingId ? `${API_URL}/cheque-transmittals/${editingId}` : `${API_URL}/cheque-transmittals`;
      const method = editingId ? 'PUT' : 'POST';
      const payload = {
        ...form,
        received_by: form.received_by || null
      };
      const res = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        fetchTransmittals();
        setShowForm(false);
        setEditingId(null);
      } else {
        const errData = await res.json();
        alert('Error: ' + (errData.error || JSON.stringify(errData)));
      }
    } catch (err) {
      console.error('Error saving transmittal:', err);
    }
  };

  const handleEdit = (t) => {
    // Note: the backend `/api/cheque-transmittals` get request now embeds `t.cheques` 
    // Wait, on editing, these cheques are already assigned to this transmittal, so they won't show in available.
    // That's fine, they are in the form payload.
    setForm({
      branch_id: t.branch_id || '',
      status: t.status || 'pending',
      transmission_date: t.transmission_date ? new Date(t.transmission_date).toISOString().split('T')[0] : '',
      received_date: t.received_date ? new Date(t.received_date).toISOString().split('T')[0] : '',
      received_by: t.received_by || '',
      notes: t.notes || '',
      cheques: t.cheques ? t.cheques.map(c => ({
        id: c.id,
        cheque_number: c.cheque_number,
        contact_id: c.contact_id || '',
        payee: c.payee || '',
        signatory: c.signatory || ''
      })) : []
    });

    if (t.branch_id) {
      fetchAvailableCheques(t.branch_id);
    } else {
      setAvailableCheques([]);
    }

    setEditingId(t.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this transmittal? (Cheques linked will just be unlinked from this transmittal)')) return;
    try {
      await fetchWithAuth(`${API_URL}/cheque-transmittals/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      fetchTransmittals();
    } catch (err) {
      console.error('Error deleting transmittal:', err);
    }
  };

  const handleAction = async (id, actionStr) => {
    if (!window.confirm(`Are you sure you want to ${actionStr} this transmittal?`)) return;
    try {
      const res = await fetchWithAuth(`${API_URL}/cheque-transmittals/${id}/${actionStr}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        fetchTransmittals();
      }
    } catch (err) {
      console.error(`Error confirming action ${actionStr}:`, err);
    }
  };

  // derived state
  // const managementUsers = users.filter(u => u.role_name && u.role_name.toLowerCase() === 'management');

  const handleView = (t) => {
    setViewingTransmittal(t);
  };

  const getCardStyle = (statusColor, targetStatus) => ({
    padding: '12px 20px',
    backgroundColor: targetStatus === '' ? '#e0e0e0' : statusColor,
    borderRadius: '8px',
    cursor: 'pointer',
    border: '2px solid',
    borderColor: filter.status === targetStatus ? '#4f46e5' : 'transparent',
    transition: 'all 0.2s',
    transform: filter.status === targetStatus ? 'scale(1.05)' : 'scale(1)',
    boxShadow: filter.status === targetStatus ? '0 4px 6px -1px rgba(0, 0, 0, 0.1)' : 'none',
    flex: 1,
    textAlign: 'center'
  });

  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.title}>Cheque Transmittals</h1>
        <button onClick={() => {
          setShowForm(true);
          setEditingId(null);
          setForm({ branch_id: '', status: 'pending', transmission_date: new Date().toISOString().split('T')[0], received_date: '', received_by: '', notes: '', cheques: [] });
          setAvailableCheques([]);
        }} style={styles.button}>
          <Plus style={{ width: '20px', height: '20px' }} /> Add Transmittal
        </button>
      </div>

      <Modal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title={`${editingId ? 'Edit' : 'Create'} Transmittal`}
        maxWidth="800px"
      >
        <div style={styles.grid2}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Branch *</label>
            <select value={form.branch_id} onChange={(e) => handleBranchChange(e.target.value)} style={styles.input}>
              <option value="">Select Branch</option>
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Transmission Date</label>
            <input
              type="date"
              value={form.transmission_date}
              min={getMinDate(form.branch_id, postedPeriods)}
              onChange={(e) => setForm({ ...form, transmission_date: e.target.value })}
              style={{
                ...styles.input,
                borderColor: isPeriodRestricted(form.branch_id, form.transmission_date, postedPeriods) ? '#ef4444' : '#d1d5db'
              }}
            />
          </div>

          <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
            <label style={styles.label}>Notes</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} style={{ ...styles.input, minHeight: '60px' }}></textarea>
          </div>
        </div>

        <hr style={{ margin: '24px 0', borderColor: '#e5e7eb' }} />

        <h3 style={{ fontSize: '18px', fontWeight: 'semibold', marginBottom: '12px' }}>Attach Cheques</h3>

        <div style={{ marginBottom: '16px', display: 'flex', gap: '8px' }}>
          <select id="available-cheques-select" style={styles.input} disabled={!form.branch_id || availableCheques.length === 0}>
            <option value="">{form.branch_id ? (availableCheques.length ? 'Available cheques...' : 'No available cheques') : 'Select branch first'}</option>
            {availableCheques.map(c => <option key={c.id} value={c.id}>{c.cheque_number} {c.bank_account_name ? `(${c.bank_account_name} - ${c.bank_account_number})` : ''}</option>)}
          </select>
          <button
            onClick={() => {
              const sel = document.getElementById('available-cheques-select');
              if (sel.value) handleAddCheque(sel.value);
              sel.value = "";
            }}
            style={styles.button}
            disabled={!form.branch_id || availableCheques.length === 0}
          >
            <Plus size={16} /> Add Cheque
          </button>
        </div>

        {form.cheques.length > 0 ? (
          <div style={{ ...styles.card, padding: 0, overflow: 'hidden' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Cheque Num</th>
                  <th style={styles.th}>Contact</th>
                  <th style={styles.th}>Payee</th>
                  <th style={styles.th}>Signatory</th>
                  <th style={styles.th}></th>
                </tr>
              </thead>
              <tbody>
                {form.cheques.map(item => (
                  <tr key={item.id}>
                    <td style={{ ...styles.td, fontWeight: '500' }}>{item.cheque_number}</td>
                    <td style={styles.td}>
                      <select value={item.contact_id} onChange={e => {
                        const contactId = e.target.value;
                        const contact = contacts.find(c => c.id === parseInt(contactId));
                        const updatedCheques = form.cheques.map(c => {
                          if (c.id === item.id) {
                            return { ...c, contact_id: contactId, ...(contact ? { payee: contact.name } : {}) };
                          }
                          return c;
                        });
                        setForm({ ...form, cheques: updatedCheques });
                      }} style={{ ...styles.input, padding: '4px' }}>
                        <option value="">None</option>
                        {contacts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </td>
                    <td style={styles.td}>
                      <input type="text" value={item.payee} onChange={e => handleChequeFieldChange(item.id, 'payee', e.target.value)} style={{ ...styles.input, padding: '4px' }} placeholder="Payee name" />
                    </td>
                    <td style={styles.td}>
                      <select value={item.signatory} onChange={e => handleChequeFieldChange(item.id, 'signatory', e.target.value)} style={{ ...styles.input, padding: '4px' }}>
                        <option value="">Select Signatory</option>
                        {users.map(m => <option key={m.id} value={m.username}>{m.username}</option>)}
                      </select>
                    </td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>
                      <button onClick={() => handleRemoveCheque(item.id)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}><X size={16} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280', backgroundColor: '#f9fafb', border: '1px dashed #d1d5db', borderRadius: '4px' }}>
            No cheques attached yet.
          </div>
        )}

        <div style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button onClick={() => setShowForm(false)} style={{ ...styles.button, ...styles.buttonSecondary }}>Cancel</button>
          <button onClick={handleSubmit} style={styles.button}>Save Transmittal</button>
        </div>
      </Modal>

      <Modal
        isOpen={!!viewingTransmittal}
        onClose={() => setViewingTransmittal(null)}
        title={`Transmittal Details: TR-${viewingTransmittal?.id}`}
        maxWidth="800px"
      >
        {viewingTransmittal && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '20px', marginBottom: '24px', backgroundColor: '#f9fafb', padding: '16px', borderRadius: '8px' }}>
              <div>
                <p style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', marginBottom: '4px' }}>Branch</p>
                <p style={{ fontWeight: '500' }}>{viewingTransmittal.branch_name || '-'}</p>
              </div>
              <div>
                <p style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', marginBottom: '4px' }}>Status</p>
                <span style={{
                  ...styles.badge,
                  ...(viewingTransmittal.status === 'received' ? styles.badgeGreen :
                    viewingTransmittal.status === 'in transit' ? styles.badgeBlue : styles.badgeYellow)
                }}>
                  {viewingTransmittal.status.toUpperCase()}
                </span>
              </div>
              <div>
                <p style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', marginBottom: '4px' }}>Transmission Date</p>
                <p style={{ fontWeight: '500' }}>{viewingTransmittal.transmission_date ? new Date(viewingTransmittal.transmission_date).toLocaleDateString() : '-'}</p>
              </div>
              <div>
                <p style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', marginBottom: '4px' }}>Received Details</p>
                <p style={{ fontWeight: '500' }}>
                  {viewingTransmittal.received_date ? new Date(viewingTransmittal.received_date).toLocaleDateString() : '-'}
                  {viewingTransmittal.received_by_name && ` by ${viewingTransmittal.received_by_name}`}
                </p>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <p style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', marginBottom: '4px' }}>Notes</p>
                <p style={{ fontWeight: '400', whiteSpace: 'pre-wrap' }}>{viewingTransmittal.notes || 'No notes available'}</p>
              </div>
            </div>

            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px' }}>Attached Cheques ({viewingTransmittal.cheques ? viewingTransmittal.cheques.length : 0})</h3>
            <div style={{ ...styles.card, padding: 0, overflow: 'hidden' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Cheque Number</th>
                    <th style={styles.th}>Payee / Issued To</th>
                    <th style={styles.th}>Signatory</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {viewingTransmittal.cheques && viewingTransmittal.cheques.length > 0 ? viewingTransmittal.cheques.map(item => (
                    <tr key={item.id}>
                      <td style={styles.td}>{item.cheque_number}</td>
                      <td style={styles.td}>{item.payee || '-'}</td>
                      <td style={styles.td}>{item.signatory || '-'}</td>
                      <td style={{ ...styles.td, textAlign: 'right' }}>
                        {item.amount ? new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(item.amount) : '-'}
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan="4" style={{ ...styles.td, textAlign: 'center' }}>No cheques found in this transmittal.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setViewingTransmittal(null)} style={styles.button}>Close</button>
            </div>
          </>
        )}
      </Modal>

      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', gap: '16px' }}>
          <div
            onClick={() => setFilter({ ...filter, status: filter.status === 'pending' ? '' : 'pending' })}
            style={getCardStyle('#fef3c7', 'pending')}
          >
            <span style={{ fontSize: '14px', color: '#92400e', fontWeight: '600' }}>
              Pending: {statusSummary.pending}
            </span>
          </div>
          <div
            onClick={() => setFilter({ ...filter, status: filter.status === 'received' ? '' : 'received' })}
            style={getCardStyle('#d1fae5', 'received')}
          >
            <span style={{ fontSize: '14px', color: '#065f46', fontWeight: '600' }}>
              Received: {statusSummary.received}
            </span>
          </div>
          <div
            onClick={() => setFilter({ ...filter, status: '' })}
            style={getCardStyle('#e0e0e0', '')}
          >
            <span style={{ fontSize: '14px', color: '#808080', fontWeight: '600' }}>
              Total: {statusSummary.total}
            </span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={{
        ...styles.card,
        marginBottom: '20px',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '16px'
      }}>
        <div style={styles.formGroup}>
          <label style={styles.label}>Filter by Branch</label>
          <select
            value={filter.branch_id}
            onChange={(e) => { setFilter({ ...filter, branch_id: e.target.value }); setCurrentPage(1); }}
            style={styles.input}
          >
            <option value="">All Branches</option>
            {branches.map(b => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Search</label>
          <input
            type="text"
            placeholder="Search by CT number..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            style={styles.input}
          />
        </div>
      </div>

      <div style={{ ...styles.card, padding: 0, overflow: 'hidden' }}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>ID</th>
              <th style={styles.th}>Branch</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Transmission Date</th>
              <th style={styles.th}>Cheques</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {transmittals.map(t => (
              <tr key={t.id}>
                <td style={styles.td}>TR-{t.id}</td>
                <td style={styles.td}>{t.branch_name || '-'}</td>
                <td style={styles.td}>
                  <span style={{
                    ...styles.badge,
                    ...(t.status === 'received' ? styles.badgeGreen :
                      t.status === 'in transit' ? styles.badgeBlue : styles.badgeYellow)
                  }}>
                    {t.status.toUpperCase()}
                  </span>
                </td>
                <td style={styles.td}>{t.transmission_date ? new Date(t.transmission_date).toLocaleDateString() : '-'}</td>
                <td style={styles.td}>{t.cheques ? t.cheques.length : 0} items</td>
                <td style={{ ...styles.td, textAlign: 'right' }}>
                  <button title="View Details" onClick={() => handleView(t)} style={{ ...styles.iconButton, color: '#6b7280' }}>
                    <Eye style={{ width: '18px', height: '18px' }} />
                  </button>

                  {t.status === 'pending' && canUpdate('cheque_transmittals') && (
                    <button title="Edit" onClick={() => handleEdit(t)} style={{ ...styles.iconButton, color: '#4f46e5' }}>
                      <Edit style={{ width: '18px', height: '18px' }} />
                    </button>
                  )}

                  {t.status === 'pending' && canUpdate('cheque_transmittals') && (
                    <button title="Dispatch" onClick={() => handleAction(t.id, 'dispatch')} style={{ ...styles.iconButton, color: '#3b82f6' }}>
                      <Send style={{ width: '18px', height: '18px' }} />
                    </button>
                  )}

                  {t.status === 'in transit' && canApprove('cheque_transmittals') && (
                    <button title="Receive" onClick={() => handleAction(t.id, 'receive')} style={{ ...styles.iconButton, color: '#10b981' }}>
                      <Check style={{ width: '18px', height: '18px' }} />
                    </button>
                  )}

                  {t.status === 'pending' && canDelete('cheque_transmittals') && (
                    <button title="Delete" onClick={() => handleDelete(t.id)} style={{ ...styles.iconButton, color: '#ef4444' }}>
                      <Trash2 style={{ width: '18px', height: '18px' }} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {transmittals.length === 0 && (
              <tr><td colSpan="6" style={{ ...styles.td, textAlign: 'center' }}>No transmittals found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '24px',
        padding: '16px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{ fontSize: '14px', color: '#6b7280' }}>
          Showing {totalCount === 0 ? 0 : Math.min((currentPage - 1) * perPage + 1, totalCount)} to {Math.min(currentPage * perPage, totalCount)} of {totalCount} entries
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginRight: '16px' }}>
            <span style={{ fontSize: '14px', color: '#6b7280' }}>Rows per page:</span>
            <select
              value={perPage}
              onChange={(e) => {
                setPerPage(parseInt(e.target.value));
                setCurrentPage(1);
              }}
              style={{
                padding: '4px 8px',
                borderRadius: '4px',
                border: '1px solid #d1d5db',
                fontSize: '14px'
              }}
            >
              {[5, 10, 25, 50, 100].map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', gap: '4px' }}>
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              style={{
                ...styles.iconButton,
                padding: '6px',
                opacity: currentPage === 1 ? 0.5 : 1,
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                border: '1px solid #d1d5db'
              }}
            >
              <ChevronsLeft size={16} />
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              style={{
                ...styles.iconButton,
                padding: '6px',
                opacity: currentPage === 1 ? 0.5 : 1,
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                border: '1px solid #d1d5db'
              }}
            >
              <ChevronLeft size={16} />
            </button>

            <div style={{ display: 'flex', gap: '4px', margin: '0 8px' }}>
              {[...Array(totalPages || 0)].map((_, i) => {
                const pageNum = i + 1;
                if (
                  pageNum === 1 ||
                  pageNum === totalPages ||
                  (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '4px',
                        border: '1px solid #d1d5db',
                        backgroundColor: currentPage === pageNum ? '#4f46e5' : 'white',
                        color: currentPage === pageNum ? 'white' : '#374151',
                        fontSize: '14px',
                        cursor: 'pointer'
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                } else if (
                  (pageNum === 2 && currentPage > 3) ||
                  (pageNum === totalPages - 1 && currentPage < totalPages - 2)
                ) {
                  return <span key={pageNum} style={{ padding: '6px' }}>...</span>;
                }
                return null;
              })}
            </div>

            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages || totalPages === 0}
              style={{
                ...styles.iconButton,
                padding: '6px',
                opacity: (currentPage === totalPages || totalPages === 0) ? 0.5 : 1,
                cursor: (currentPage === totalPages || totalPages === 0) ? 'not-allowed' : 'pointer',
                border: '1px solid #d1d5db'
              }}
            >
              <ChevronRight size={16} />
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages || totalPages === 0}
              style={{
                ...styles.iconButton,
                padding: '6px',
                opacity: (currentPage === totalPages || totalPages === 0) ? 0.5 : 1,
                cursor: (currentPage === totalPages || totalPages === 0) ? 'not-allowed' : 'pointer',
                border: '1px solid #d1d5db'
              }}
            >
              <ChevronsRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChequeTransmittals;
